import React from 'react';
import type { ProductLabelLayout, ProductWorkspaceDetail } from './productLabelTypes';
import type { LabelFieldKey, LabelMetrics } from './labelLayoutEngine';
import { maxCharsForField, truncateToFit } from './labelLayoutEngine';
import type { LabelFieldContext } from './labelFieldRegistry';
import { getEnabledFieldOrder, getRenderableFieldOrder } from './labelFieldRegistry';

export type LabelFieldRenderProps = {
  product: Pick<
    ProductWorkspaceDetail,
    | 'sku'
    | 'name'
    | 'description'
    | 'benefits'
    | 'imageUrl'
    | 'unitLabel'
    | 'baseUnit'
    | 'hsCode'
    | 'countryOfOrigin'
    | 'typicalExpiryDays'
    | 'category'
  >;
  layout: ProductLabelLayout;
  metrics: LabelMetrics;
  sale: number;
  unit: string;
  originName: string | null;
  packagingText: string | null;
  netQuantityLabel: string | null;
  barcodeRef: React.RefObject<SVGSVGElement | null>;
  currency: string;
  formatPrice: (n: number) => string;
  fieldCtx: LabelFieldContext;
};

function LabelBlock({
  metrics,
  title,
  children,
  muted,
  bordered,
  layout,
}: {
  metrics: LabelMetrics;
  title?: string;
  children: React.ReactNode;
  muted?: boolean;
  bordered?: boolean;
  layout: ProductLabelLayout;
}) {
  return (
    <div
      className="min-h-0 min-w-0 overflow-hidden"
      style={{
        fontSize: metrics.smallFont,
        lineHeight: 1.25,
        padding: metrics.gap * 0.5,
        backgroundColor: muted ? layout.secondaryBackgroundColor : undefined,
        border: bordered ? `1px solid ${layout.panelBorderColor}` : undefined,
        color: muted ? layout.legalPanelTextColor : layout.textColor,
      }}
    >
      {title ? (
        <div
          className="mb-0.5 font-bold uppercase tracking-wide"
          style={{ fontSize: metrics.smallFont * 0.9, opacity: 0.85 }}
        >
          {title}
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function renderLabelField(
  key: LabelFieldKey,
  props: LabelFieldRenderProps,
): React.ReactNode {
  const {
    product,
    layout,
    metrics: m,
    sale,
    unit,
    originName,
    packagingText,
    netQuantityLabel,
    barcodeRef,
    currency,
    formatPrice,
  } = props;
  const fs = (n: number) => `${n}px`;

  switch (key) {
    case 'brand':
      return layout.showBrand ? (
        <div
          style={{
            fontSize: fs(m.titleFont),
            fontWeight: 700,
            color: layout.accentColor,
            lineHeight: 1.1,
          }}
        >
          {truncateToFit(layout.brandName, maxCharsForField(m, 'brand'))}
        </div>
      ) : null;
    case 'name':
      return layout.showName ? (
        <div style={{ fontSize: fs(m.baseFont * 1.1), fontWeight: 600, lineHeight: 1.15 }}>
          {truncateToFit(product.name, maxCharsForField(m, 'name'))}
        </div>
      ) : null;
    case 'image':
      return layout.showImage && product.imageUrl ? (
        <img
          src={product.imageUrl}
          alt=""
          style={{ width: m.imageSize, height: m.imageSize, objectFit: 'cover' }}
          className="shrink-0 border"
        />
      ) : null;
    case 'extraImage0':
    case 'extraImage1': {
      const idx = key === 'extraImage0' ? 0 : 1;
      const img = layout.extraImages[idx];
      return img?.url ? (
        <img
          src={img.url}
          alt={img.label || ''}
          title={img.label}
          style={{ width: m.extraImageSize, height: m.extraImageSize, objectFit: 'contain' }}
          className="shrink-0"
        />
      ) : null;
    }
    case 'price':
      return layout.showPrice && sale > 0 ? (
        <div style={{ fontSize: fs(m.baseFont), fontWeight: 700, color: layout.accentColor }}>
          {currency} {formatPrice(sale)}
          {layout.showUnit ? ` / ${unit}` : ''}
        </div>
      ) : null;
    case 'netQty':
      return layout.showNetQuantity && netQuantityLabel ? (
        <div style={{ fontSize: fs(m.smallFont), fontWeight: 600 }}>{netQuantityLabel}</div>
      ) : null;
    case 'category':
      return layout.showCategory ? (
        <div style={{ fontSize: fs(m.smallFont) }}>
          <span className="font-semibold">Category:</span> {product.category?.name || '—'}
        </div>
      ) : null;
    case 'description':
      return layout.showDescription ? (
        <div style={{ fontSize: fs(m.smallFont) }}>
          <span className="font-semibold">Description:</span>{' '}
          {product.description
            ? truncateToFit(product.description, maxCharsForField(m, 'description'))
            : '—'}
        </div>
      ) : null;
    case 'benefits':
      return layout.showBenefits && product.benefits ? (
        <div style={{ fontSize: fs(m.smallFont), color: layout.accentColor }}>
          {truncateToFit(product.benefits, maxCharsForField(m, 'benefits'))}
        </div>
      ) : null;
    case 'hsCode':
      return layout.showHsCode ? (
        <div style={{ fontSize: fs(m.smallFont) }}>
          <span className="font-semibold">HS:</span> {product.hsCode || '—'}
        </div>
      ) : null;
    case 'origin':
      return layout.showCountryOfOrigin ? (
        <div style={{ fontSize: fs(m.smallFont) }}>
          <span className="font-semibold">Origin:</span>{' '}
          {product.countryOfOrigin ? `${originName} (${product.countryOfOrigin})` : '—'}
        </div>
      ) : null;
    case 'expiry':
      return layout.showExpiry ? (
        <div style={{ fontSize: fs(m.smallFont) }}>
          <span className="font-semibold">Shelf life:</span>{' '}
          {product.typicalExpiryDays != null
            ? `${Number(product.typicalExpiryDays).toFixed(0)} days`
            : '—'}
        </div>
      ) : null;
    case 'packaging':
      return layout.showPackaging && packagingText ? (
        <div style={{ fontSize: fs(m.smallFont) }}>
          <span className="font-semibold">Pack:</span> {packagingText}
        </div>
      ) : null;
    case 'company':
      return layout.showCompanyInfo &&
        (layout.companyPhone || layout.companyWebsite || layout.footerText) ? (
        <LabelBlock metrics={m} layout={layout}>
          {layout.companyPhone ? <div>{layout.companyPhone}</div> : null}
          {layout.companyWebsite ? <div>{layout.companyWebsite}</div> : null}
        </LabelBlock>
      ) : null;
    case 'marketing':
      return layout.showMarketing && (layout.marketingTagline || layout.marketingHashtag) ? (
        <LabelBlock metrics={m} layout={layout}>
          {layout.marketingTagline ? <div>{layout.marketingTagline}</div> : null}
          {layout.marketingHashtag ? (
            <div style={{ fontWeight: 700, color: layout.accentColor }}>
              {layout.marketingHashtag}
            </div>
          ) : null}
        </LabelBlock>
      ) : null;
    case 'barcode':
      return layout.showBarcode ? (
        <svg ref={barcodeRef} style={{ maxHeight: m.barcodeHeight, width: '100%' }} />
      ) : null;
    case 'sku':
      return layout.showSku ? (
        <div style={{ fontSize: fs(m.smallFont), fontFamily: 'monospace' }}>{product.sku}</div>
      ) : null;
    case 'legal':
      return layout.companyLegalLine ? (
        <div
          className="text-center uppercase"
          style={{ fontSize: fs(m.smallFont * 0.85), opacity: 0.9 }}
        >
          {truncateToFit(layout.companyLegalLine, maxCharsForField(m, 'legal'))}
        </div>
      ) : null;
    case 'footer':
      return layout.footerText ? (
        <div style={{ fontSize: fs(m.smallFont) }}>
          {truncateToFit(layout.footerText, maxCharsForField(m, 'footer'))}
        </div>
      ) : null;
    default:
      return null;
  }
}

export function LabelFieldOrderStack({
  props,
  wrapField,
  editMode = false,
}: {
  props: LabelFieldRenderProps;
  wrapField?: (key: LabelFieldKey, node: React.ReactNode) => React.ReactNode;
  editMode?: boolean;
}) {
  const { layout, metrics, fieldCtx } = props;
  const keys = editMode
    ? getEnabledFieldOrder(layout)
    : getRenderableFieldOrder(layout, fieldCtx);

  return (
    <div
      className="flex h-full w-full min-h-0 flex-col"
      style={{ padding: metrics.padding, gap: metrics.gap }}
    >
      {keys.map((key) => {
        let node = renderLabelField(key, props);
        if (!node && editMode) {
          node = (
            <div
              className="rounded border border-dashed border-slate-300 px-2 py-1 text-center text-[10px] text-slate-400"
              style={{ fontSize: metrics.smallFont }}
            >
              {key} (empty)
            </div>
          );
        }
        if (!node) return null;
        return (
          <div key={key} className="min-h-0 min-w-0 shrink-0">
            {wrapField ? wrapField(key, node) : node}
          </div>
        );
      })}
    </div>
  );
}
